import type { TupleToUnion } from 'type-fest'

const themeBaseBorderColorNames = [
  'brand',
  'extra-muted',
  'muted',
  'medium',
  'bright',
  'extra-bright',
  'accent',
  'positive',
  'negative',
  'caution',
  'info',
  'transparent',
  'notice',
] as const

const themeBorderColorNames = [
  'dashed-rounded-primary-blue',
  'negative-bright',
  'notice-bright',
  'positive-bright',
  'notice-extra-bright',
  'positive-extra-bright',
  'notice-muted',
  'positive-muted',
  ...themeBaseBorderColorNames,
] as const
type ThemeBorderColor = TupleToUnion<typeof themeBorderColorNames>

const themeRingColorNames = themeBaseBorderColorNames
type ThemeRingColor = TupleToUnion<typeof themeRingColorNames>

const themeDivideColorNames = themeBaseBorderColorNames
type ThemeDivideColor = TupleToUnion<typeof themeDivideColorNames>

const themeHoverBorderColorNames = [
  'cta',
  'secondary',
  'extra-muted',
  'muted',
  'negative',
  'notice',
  'positive',
  'transparent',
  'negative-bright',
  'notice-extra-bright',
  'positive-extra-bright',
] as const
type ThemeHoverBorderColor = TupleToUnion<typeof themeHoverBorderColorNames>

const themeFocusBorderColorNames = [
  'muted',
  'caution',
  'positive',
  'negative',
  'notice',
] as const
type ThemeFocusBorderColor = TupleToUnion<typeof themeFocusBorderColorNames>

const themeActiveBorderColorNames = [
  'cta',
  'secondary',
  'negative',
  'notice',
  'positive',
  'negative-dark',
  'notice-dark',
  'positive-dark',
] as const
type ThemeActiveBorderColor = TupleToUnion<typeof themeActiveBorderColorNames>

const themeDisabledBorderColorNames = [
  'cta',
  'secondary',
  'muted',
  'negative',
  'notice',
  'positive',
  'negative-extra-bright',
  'notice-extra-bright',
  'positive-extra-bright',
] as const
type ThemeDisabledBorderColor = TupleToUnion<
  typeof themeDisabledBorderColorNames
>

const themeReadonlyBorderColorNames = ['muted'] as const
type ThemeReadonlyBorderColor = TupleToUnion<
  typeof themeReadonlyBorderColorNames
>

const themeBorderColorMap: Record<ThemeBorderColor, string> = {
  brand: 'border-brand',
  'dashed-rounded-primary-blue': 'bg-primary-blue-rounded-dashed-border',
  'extra-muted': 'border-white/5',
  muted: 'border-white/10',
  medium: 'border-white/30',
  bright: 'border-white/50',
  'extra-bright': 'border-white',
  accent: 'border-primary-blue/30',
  negative: 'border-primary-red',
  caution: 'border-primary-yellow',
  positive: 'border-primary-green',
  info: 'border-primary-blue',
  transparent: 'border-transparent',
  notice: 'border-primary-purple',
  'negative-bright': 'border-red-200',
  'notice-bright': 'border-purple-100',
  'positive-bright': 'border-green-200',
  'notice-extra-bright': 'border-purple-50',
  'positive-extra-bright': 'border-green-50',
  'notice-muted': 'border-purple-200/50',
  'positive-muted': 'border-green-200/50',
}

const themeRingColorMap: Record<ThemeRingColor, string> = {
  brand: 'ring-brand',
  'extra-muted': 'ring-white/5',
  muted: 'ring-white/10',
  medium: 'ring-white/30',
  bright: 'ring-white/50',
  'extra-bright': 'ring-white',
  accent: 'ring-primary-blue/30',
  negative: 'ring-primary-red',
  caution: 'ring-primary-yellow',
  positive: 'ring-primary-green',
  info: 'ring-primary-blue',
  transparent: 'ring-transparent',
  notice: 'ring-notice',
}

const themeDivideColorMap: Record<ThemeRingColor, string> = {
  brand: 'divide-brand',
  'extra-muted': 'divide-white/5',
  muted: 'divide-white/10',
  medium: 'divide-white/30',
  bright: 'divide-white/50',
  'extra-bright': 'divide-white',
  accent: 'divide-primary-blue/30',
  negative: 'divide-primary-red',
  caution: 'divide-primary-yellow',
  positive: 'divide-primary-green',
  info: 'divide-primary-blue',
  transparent: 'divide-transparent',
  notice: 'divide-notice',
}

const themeHoverBorderColorMap: Record<ThemeHoverBorderColor, string> = {
  cta: 'hover:border-white',
  secondary: 'hover:border-subtle',
  'extra-muted': 'hover:border-white/10',
  muted: 'hover:border-white/50',
  negative: 'hover:border-primary-red',
  notice: 'hover:border-primary-purple',
  positive: 'hover:border-primary-green',
  transparent: 'border-transparent',
  'negative-bright': 'hover:border-red-200',
  'notice-extra-bright': 'hover:border-purple-50',
  'positive-extra-bright': 'hover:border-green-50',
}

const themeHoverRingColorMap: Record<ThemeHoverBorderColor, string> = {
  cta: 'hover:ring-white',
  secondary: 'hover:ring-subtle',
  'extra-muted': 'hover:ring-white/10',
  muted: 'hover:ring-white/50',
  negative: 'focus:ring-primary-red',
  notice: 'hover:ring-primary-purple',
  positive: 'hover:ring-primary-green',
  transparent: 'border-transparent',
  'negative-bright': 'hover:border-red-200',
  'notice-extra-bright': 'hover:border-purple-50',
  'positive-extra-bright': 'hover:border-green-50',
}

const themeFocusBorderColorMap: Record<ThemeFocusBorderColor, string> = {
  muted: 'focus:border-primary-blue',
  negative: 'focus:border-primary-red',
  caution: 'focus:border-primary-yellow',
  positive: 'focus:border-primary-green',
  notice: 'focus:border-primary-purple',
}

const themeFocusRingColorMap: Record<ThemeFocusBorderColor, string> = {
  muted: 'focus:ring-primary-blue',
  negative: 'focus:ring-primary-red',
  caution: 'focus:ring-primary-yellow',
  positive: 'focus:ring-primary-green',
  notice: 'focus:ring-primary-purple',
}

const themeFocusWithinBorderColorMap: Record<ThemeFocusBorderColor, string> = {
  muted: 'focus-within:border-primary-blue',
  negative: 'focus-within:border-primary-red',
  caution: 'focus-within:border-primary-yellow',
  positive: 'focus-within:border-primary-green',
  notice: 'focus-within:border-primary-purple',
}

const themeFocusWithinRingColorMap: Record<ThemeFocusBorderColor, string> = {
  muted: 'focus-within:ring-primary-blue',
  negative: 'focus-within:ring-primary-red',
  caution: 'focus-within:ring-primary-yellow',
  positive: 'focus-within:ring-primary-green',
  notice: 'focus-within:ring-primary-purple',
}

const themeActiveBorderColorMap: Record<ThemeActiveBorderColor, string> = {
  cta: 'active:border-white/5',
  secondary: 'active:border-subtle',
  negative: 'active:border-primary-red',
  notice: 'active:border-primary-purple',
  positive: 'active:border-primary-green',
  'negative-dark': 'active:border-red-700',
  'notice-dark': 'active:border-purple-400',
  'positive-dark': 'active:border-green-700',
}

const themeDisabledBorderColorMap: Record<ThemeDisabledBorderColor, string> = {
  cta: 'disabled:border-white/5 data-[disabled]:border-white/5',
  secondary: 'disabled:border-subtle data-[disabled]:border-subtle',
  muted: 'disabled:border-black/30 data-[disabled]:border-black/30',
  negative: 'disabled:border-primary-red data-[disabled]:border-primary-red',
  notice:
    'disabled:border-primary-purple data-[disabled]:border-primary-purple',
  positive:
    'disabled:border-primary-green data-[disabled]:border-primary-green',
  'negative-extra-bright':
    'disabled:border-red-50/5 data-[disabled]:border-red-50/5',
  'notice-extra-bright':
    'disabled:border-purple-50/5 data-[disabled]:border-purple-50/5',
  'positive-extra-bright':
    'disabled:border-green-50/5 data-[disabled]:border-green-50/5',
}

const themeDisabledRingColorMap: Record<ThemeDisabledBorderColor, string> = {
  cta: 'disabled:ring-white/5 data-[disabled]:ring-white/5',
  secondary: 'disabled:ring-white/5 data-[disabled]:ring-white/5',
  muted: 'disabled:ring-black/30 data-[disabled]:ring-black/30',
  negative: 'disabled:ring-primary-red data-[disabled]:ring-primary-red',
  notice: 'disabled:ring-primary-purple data-[disabled]:ring-primary-purple',
  positive: 'disabled:ring-primary-green data-[disabled]:ring-primary-green',
  'negative-extra-bright':
    'disabled:border-red-50/5 data-[disabled]:border-red-50/5',
  'notice-extra-bright':
    'disabled:border-purple-50/5 data-[disabled]:border-purple-50/5',
  'positive-extra-bright':
    'disabled:border-green-50/5 data-[disabled]:border-green-50/5',
}

const themeReadonlyBorderColorMap: Record<ThemeReadonlyBorderColor, string> = {
  muted: 'aria-readonly:border-black/5',
}

const themeReadonlyRingColorMap: Record<ThemeReadonlyBorderColor, string> = {
  muted: 'aria-readonly:ring-black/5',
}

export {
  type ThemeActiveBorderColor,
  type ThemeBorderColor,
  type ThemeDisabledBorderColor,
  type ThemeDivideColor,
  type ThemeFocusBorderColor,
  type ThemeHoverBorderColor,
  type ThemeRingColor,
  themeActiveBorderColorMap,
  themeBorderColorMap,
  themeBorderColorNames,
  themeDisabledBorderColorMap,
  themeDisabledRingColorMap,
  themeDivideColorMap,
  themeFocusBorderColorMap,
  themeFocusRingColorMap,
  themeFocusWithinBorderColorMap,
  themeFocusWithinRingColorMap,
  themeHoverBorderColorMap,
  themeHoverRingColorMap,
  themeReadonlyBorderColorMap,
  themeReadonlyRingColorMap,
  themeRingColorMap,
}
